/**
 * Ostap PDP — sofa photo per Farbe + Stoff grouping.
 * Auto-detect:
 *  - swatch = variant-attached media / Stoffmuster alt / media→Farbe map
 *  - color key = option «Farbe» (matched via map, alt, or filename)
 *  - Stoff = everything before the trailing color code (supports «Now Or Never»)
 * Stage 4: Ecke Links/Rechts via alt (fallback: order).
 */
(function () {
  'use strict';

  const COLOR_OPTION_NAMES = new Set(['farbe', 'color', 'couleur', 'colour']);
  const ECKE_OPTION_NAMES = new Set(['ecke', 'corner', 'seite']);
  /** Legacy filename fallback for old Abriamo/Tilia assets */
  const LEGACY_SWATCH_RE = /\/(ABRIAMO|TILIA|DIOSA|ABRAMO)-(\d+)/i;

  function readConfig() {
    const el = document.querySelector('[data-ostap-variant-media-config]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  function normKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function compactKey(value) {
    return normKey(value).replace(/\s+/g, '');
  }

  /**
   * «Now Or Never 83» → stoff «Now Or Never», code «83»
   * «Abriamo14» → stoff «Abriamo», code «14» (glued typo)
   */
  function parseFarbeValue(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    let m = raw.match(/^(.+?)\s+(\d+[A-Za-z]*)$/);
    if (m) {
      return { stoff: m[1].trim(), farbe: raw, code: m[2] };
    }

    m = raw.match(/^([A-Za-z]+)(\d+[A-Za-z]*)$/);
    if (m) {
      return { stoff: m[1], farbe: raw, code: m[2], glued: true };
    }

    return null;
  }

  function slideImgUrl(slide) {
    const img = slide.querySelector('img[data-master], img[data-src], img[src]');
    if (!img) return '';
    return img.getAttribute('data-master') || img.getAttribute('data-src') || img.getAttribute('src') || '';
  }

  function slideAlt(slide) {
    const img = slide.querySelector('img');
    return img ? img.getAttribute('alt') || '' : '';
  }

  function slideFilename(slide) {
    const url = slideImgUrl(slide);
    const path = (url || '').split('?')[0];
    const name = path.split('/').pop() || '';
    try {
      return decodeURIComponent(name);
    } catch (e) {
      return name;
    }
  }

  function getProductRoot() {
    return document.querySelector('[data-product-featured]');
  }

  function getGallery(root) {
    return root && root.querySelector('[data-t4s-gallery][data-main-media]');
  }

  function getFlickity(gallery) {
    if (!gallery) return null;
    if (window.Flickity) {
      const inst = window.Flickity.data(gallery);
      if (inst) return inst;
    }
    const jq = window.jQuery_T4NT || window.jQuery;
    if (jq) {
      const data = jq(gallery).data('flickityt4s') || jq(gallery).data('flickity');
      if (data) return data;
    }
    return null;
  }

  function collectFarbeValues(root, config) {
    const fromConfig = Array.isArray(config.farbeValues) ? config.farbeValues.slice() : [];
    const fromDom = Array.from(root.querySelectorAll('[data-swatch-option]')).reduce((acc, opt) => {
      const title = opt.querySelector('.t4s-swatch__title');
      if (!title) return acc;
      const name = title.textContent.split(':')[0].trim().toLowerCase();
      if (!COLOR_OPTION_NAMES.has(name)) return acc;
      opt.querySelectorAll('[data-swatch-item]').forEach((el) => {
        if (el.dataset.value) acc.push(el.dataset.value);
      });
      return acc;
    }, []);

    const seen = new Set();
    const out = [];
    fromConfig.concat(fromDom).forEach((v) => {
      const key = normKey(v);
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(v);
    });
    out.sort((a, b) => b.length - a.length);
    return out;
  }

  function resolveFarbeFromText(text, farbeValues) {
    const raw = String(text || '');
    if (!raw || !farbeValues.length) return null;
    const lower = raw.toLowerCase();
    const compact = lower.replace(/\s+/g, '');

    for (let i = 0; i < farbeValues.length; i++) {
      const fv = farbeValues[i];
      const n = normKey(fv);
      const c = compactKey(fv);
      if (n && lower.indexOf(n) !== -1) return fv;
      if (c && compact.indexOf(c) !== -1) return fv;
    }
    return null;
  }

  function fabricAliases(stoff) {
    const s = compactKey(stoff);
    const out = [s];
    // Merchant files use «Dress Me» while option is «Drees Me»
    if (s.indexOf('drees') !== -1) out.push(s.replace(/drees/g, 'dress'));
    if (s.indexOf('dress') !== -1) out.push(s.replace(/dress/g, 'drees'));
    return out.filter(function (v, i, a) {
      return v && a.indexOf(v) === i;
    });
  }

  function codeAliases(code) {
    const c = String(code || '');
    const out = [c];
    if (/^\d+$/.test(c)) {
      const n = String(parseInt(c, 10));
      out.push(n);
      if (n.length === 1) out.push('0' + n);
      if (n.length === 2 && c.length === 1) out.push(c);
    }
    return out.filter(function (v, i, a) {
      return v && a.indexOf(v) === i;
    });
  }

  /**
   * Extract Dress/Drees Me color code from merchant filenames.
   * - dressme_1_2_L / dressme_1_12_L → 2 / 12 (series prefix «1»)
   * - dressme_16_1-L → 16
   * - tkanina-…-Dress-Me-2-… → 2
   */
  function extractDressMeCode(base) {
    const b = String(base || '').toLowerCase();
    if (!/dress[\s_-]*me|drees[\s_-]*me|dressme|dreesme/.test(b)) return null;
    let m = b.match(/dressme_1_(\d+)|dreesme_1_(\d+)|dress[\s_-]*me[\s_-]*1[\s_-]+(\d+)|drees[\s_-]*me[\s_-]*1[\s_-]+(\d+)/i);
    if (m) return m[1] || m[2] || m[3] || m[4] || null;
    m = b.match(/dressme_(\d+)_1\s*-|dreesme_(\d+)_1\s*-|dress[\s_-]*me[\s_-]*(\d+)[\s_-]+1\s*-/i);
    if (m) return m[1] || m[2] || m[3] || null;
    m = b.match(/dress[\s_-]*me[\s_-]*(\d+)|drees[\s_-]*me[\s_-]*(\d+)/i);
    if (m) return m[1] || m[2] || null;
    return null;
  }

  function farbeByCode(farbeValues, code) {
    if (!code) return null;
    const want = codeAliases(code);
    const hits = [];
    farbeValues.forEach(function (fv) {
      const parsed = parseFarbeValue(fv);
      if (!parsed || !parsed.code) return;
      const stoff = compactKey(parsed.stoff);
      if (stoff.indexOf('drees') === -1 && stoff.indexOf('dress') === -1) return;
      const aliases = codeAliases(parsed.code);
      if (want.some(function (w) { return aliases.indexOf(w) !== -1; })) hits.push(fv);
    });
    return hits.length === 1 ? hits[0] : null;
  }

  function resolveFarbeFromFilename(filename, farbeValues) {
    const base = String(filename || '')
      .toLowerCase()
      .replace(/\.[a-z0-9]+$/i, '');
    if (!base) return null;
    // «dress-me-2» and «dressme_1_2» both compact to contain «dressme2»
    const baseCompact = base.replace(/[^a-z0-9]+/g, '');

    // Prefer structured Dress/Drees Me parse (avoids dressme_1_2 → false «12»)
    const dressCode = extractDressMeCode(base);
    if (dressCode) {
      const dressHit = farbeByCode(farbeValues, dressCode);
      if (dressHit) return dressHit;
    }

    for (let i = 0; i < farbeValues.length; i++) {
      const fv = farbeValues[i];
      const slug = normKey(fv).replace(/\s+/g, '-');
      const unders = normKey(fv).replace(/\s+/g, '_');
      if (slug && base.indexOf(slug) !== -1) return fv;
      if (unders && base.indexOf(unders) !== -1) return fv;

      // «Drees Me 02» ↔ dress-me-2 / Dress-Me-02 / tkanina-…-Dress-Me-2-…
      const parsed0 = parseFarbeValue(fv);
      if (parsed0 && parsed0.code) {
        const fabs = fabricAliases(parsed0.stoff);
        const codes = codeAliases(parsed0.code);
        for (let fi = 0; fi < fabs.length; fi++) {
          const fab = fabs[fi];
          if (!fab) continue;
          const hasFab =
            base.indexOf(fab) !== -1 ||
            baseCompact.indexOf(fab) !== -1 ||
            base.indexOf(fab.replace(/me$/, '-me')) !== -1;
          if (!hasFab) continue;
          // Longer codes first so «12» wins over prefix «2» inside dressme12
          const codesLongFirst = codes.slice().sort(function (a, b) {
            return b.length - a.length;
          });
          for (let ci = 0; ci < codesLongFirst.length; ci++) {
            const code = codesLongFirst[ci];
            const token = fab + code;
            const idx = baseCompact.indexOf(token);
            if (idx !== -1) {
              const next = baseCompact.charAt(idx + token.length);
              if (!next || !/\d/.test(next)) return fv;
            }
            const reSlug = new RegExp(
              '(?:^|[^a-z0-9])' +
                fab.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                '[^a-z0-9]*' +
                code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                '(?:[^0-9]|$)'
            );
            if (reSlug.test(base)) return fv;
          }
        }
      }
    }

    // Sofa / lifestyle exports often end with _8 / _10 — never match by color code alone
    if (
      /^(cosviro|zavantazhennya|generated|ecksofa|wohnlandschaft|make_the_fabric)/i.test(base) &&
      baseCompact.indexOf('dressme') === -1 &&
      baseCompact.indexOf('dreesme') === -1
    ) {
      return null;
    }

    const codeHits = [];
    farbeValues.forEach((fv) => {
      const parsed = parseFarbeValue(fv);
      if (!parsed || !parsed.code) return;
      const fabs = fabricAliases(parsed.stoff);
      const hasFab = fabs.some(function (f) {
        return f && (base.indexOf(f) !== -1 || baseCompact.indexOf(f) !== -1);
      });
      // Require fabric name in filename when matching a color code (avoid Cosviro_10 → Abriamo 10)
      if (!hasFab) return;
      const codes = codeAliases(parsed.code);
      const codesLongFirst = codes.slice().sort(function (a, b) {
        return b.length - a.length;
      });
      const hit = codesLongFirst.some(function (code) {
        if (
          fabs.some(function (f) {
            if (!f) return false;
            const token = f + code;
            const idx = baseCompact.indexOf(token);
            if (idx === -1) return false;
            const next = baseCompact.charAt(idx + token.length);
            return !next || !/\d/.test(next);
          })
        ) {
          return true;
        }
        const re = new RegExp(
          '(?:^|[^0-9])' + code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?:[^0-9]|$)'
        );
        return re.test(base);
      });
      if (hit) codeHits.push(fv);
    });
    if (codeHits.length === 1) return codeHits[0];
    return null;
  }

  function mediaFarbeMap(config) {
    const map = Object.create(null);
    const src = config.mediaFarbeMap || {};
    Object.keys(src).forEach((id) => {
      map[String(id)] = src[id];
    });
    return map;
  }

  function fileFarbeMap(config) {
    const map = Object.create(null);
    const src = config.fileFarbeMap || {};
    Object.keys(src).forEach((name) => {
      map[String(name).toLowerCase()] = src[name];
    });
    return map;
  }

  function looksLikeSwatchFilename(filename, farbeValues) {
    const base = String(filename || '')
      .toLowerCase()
      .replace(/\.[a-z0-9]+$/i, '');
    if (!base) return false;
    // Sofa / lifestyle exports — never treat as fabric swatch
    if (/zavantazhennya|generated_image|ecksofa|wohnlandschaft|naroznik|sofa[_-]/i.test(base)) {
      return false;
    }
    if (/^(cremona|abriamo|tilia|diosa|abramo|now-or-never|baloo|dress-me|tkanina|600px|stoffmuster)/i.test(base)) {
      return true;
    }
    const fv = resolveFarbeFromFilename(filename, farbeValues);
    if (!fv) return false;
    const slug = normKey(fv).replace(/\s+/g, '-');
    return slug && base.indexOf(slug) === 0;
  }

  function isSwatchSlide(slide, mediaMap, farbeValues, fileMap) {
    const alt = slideAlt(slide);
    // Explicit sofa / lifestyle alts are never fabric swatches (even if variant-attached)
    if (/farbe_/i.test(alt) && !/stoffmuster/i.test(alt)) return false;
    if (/stoffmuster/i.test(alt)) return true;

    const mediaId = slide.getAttribute('data-media-id');
    if (mediaId && mediaMap[String(mediaId)]) return true;

    const fname = slideFilename(slide).toLowerCase();
    if (fname && fileMap && fileMap[fname]) return true;

    if (LEGACY_SWATCH_RE.test(slideImgUrl(slide))) return true;
    if (looksLikeSwatchFilename(slideFilename(slide), farbeValues)) return true;

    // Legacy: variant-attached media used to mean Stoffmuster only.
    // After cart/checkout fix, sofa photos are also variant-attached — do not hide them.
    const attach = slide.getAttribute('data-variant-attach');
    const variantMarked =
      (attach && String(attach).trim()) ||
      slide.classList.contains('t4s-product__media-item--variant');
    if (variantMarked) {
      return looksLikeSwatchFilename(slideFilename(slide), farbeValues);
    }
    return false;
  }

  function resolveSlideFarbe(slide, farbeValues, mediaMap, fileMap) {
    const mediaId = slide.getAttribute('data-media-id');
    if (mediaId && mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];

    const fname = slideFilename(slide);
    if (fname && fileMap[fname.toLowerCase()]) return fileMap[fname.toLowerCase()];

    const fromAlt = resolveFarbeFromText(slideAlt(slide), farbeValues);
    if (fromAlt) return fromAlt;

    const fromFile = resolveFarbeFromFilename(fname, farbeValues);
    if (fromFile) return fromFile;

    const legacy = slideImgUrl(slide).match(LEGACY_SWATCH_RE);
    if (legacy) {
      const num = String(legacy[2]).replace(/^0+/, '') || '0';
      const padded = num.length === 1 ? '0' + num : num;
      const fabric = legacy[1].charAt(0).toUpperCase() + legacy[1].slice(1).toLowerCase();
      const guess = fabric === 'Abramo' ? 'Abriamo' : fabric;
      return resolveFarbeFromText(guess + ' ' + padded, farbeValues) || guess + ' ' + padded;
    }
    return null;
  }

  function reloadGallery(gallery) {
    const flkty = getFlickity(gallery);
    if (!flkty || !flkty.slider) {
      window.dispatchEvent(new Event('resize'));
      return;
    }

    const slider = flkty.slider;
    const root = gallery.closest('[data-product-featured]');
    const map = root && root._ostapMediaMap;

    if (map) {
      map.slides.forEach(function (s) {
        if (slider.contains(s)) gallery.appendChild(s);
      });

      (map.leading || []).forEach(function (s) {
        if (!s.classList.contains('is--media-hide')) slider.appendChild(s);
      });
      map.slides.forEach(function (s) {
        if (s.dataset.ostapMediaType === 'color' && !s.classList.contains('is--media-hide')) {
          slider.appendChild(s);
        }
      });
      (map.shared || []).forEach(function (s) {
        if (!s.classList.contains('is--media-hide')) slider.appendChild(s);
      });
    }

    flkty.reloadCells();
    if (typeof flkty.resize === 'function') flkty.resize();
  }

  function selectSlide(gallery, slide) {
    if (!gallery || !slide) return;
    const visible = Array.from(
      gallery.querySelectorAll('[data-main-slide]:not(.is--media-hide):not(.ostap-media--swatch)')
    );
    const index = visible.indexOf(slide);
    if (index < 0) return;

    const flkty = getFlickity(gallery);
    if (flkty && typeof flkty.select === 'function') {
      flkty.select(index);
      return;
    }

    const thumb =
      gallery.closest('[data-product-single-media-group]') ||
      gallery.closest('.t4s-product__media-wrapper');
    const navItem =
      thumb &&
      thumb.querySelector('.t4s-carousel__nav [data-media-id="' + slide.getAttribute('data-media-id') + '"]');
    if (navItem) navItem.click();
  }

  function hasEckeOption(root) {
    for (const el of root.querySelectorAll('[data-swatch-option]')) {
      const title = el.querySelector('.t4s-swatch__title');
      if (!title) continue;
      const name = title.textContent.split(':')[0].trim().toLowerCase();
      if (ECKE_OPTION_NAMES.has(name)) return true;
    }
    return false;
  }

  function getSelectedEcke(root) {
    for (const input of root.querySelectorAll('.t4s-swatch-input:checked')) {
      const name = (input.name || '').toLowerCase();
      if (ECKE_OPTION_NAMES.has(name)) return input.value.toLowerCase();
    }
    return null;
  }

  const MASSE_OPTION_NAMES = new Set(['maße', 'masse', 'maß', 'größe', 'grosse', 'size', 'modell', 'model']);

  function getSelectedMasse(root) {
    if (!root) return null;
    // Prefer select title — native Maße click updates select reliably; radios can lag.
    const select = getVariantSelect(root);
    if (select && select.selectedOptions && select.selectedOptions[0]) {
      const parts = (select.selectedOptions[0].textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
        .split(/\s*\/\s*/);
      if (parts.length >= 2) {
        const last = parts[parts.length - 1].trim();
        if (last && !/^(links|rechts)$/i.test(last)) return last;
      }
    }
    for (const input of root.querySelectorAll('.t4s-swatch-input:checked')) {
      const name = (input.name || '').toLowerCase();
      const val = String(input.value || '').trim();
      if (MASSE_OPTION_NAMES.has(name) && val) return val;
      if (/^\d+\s*cm$/i.test(val)) return val;
    }
    const selected = root.querySelector(
      '[data-swatch-option].is-t4s-name__maße [data-swatch-item].is--selected, ' +
        '[data-swatch-option].is-t4s-name__Maße [data-swatch-item].is--selected, ' +
        '[data-swatch-option].is-t4s-name__masse [data-swatch-item].is--selected'
    );
    if (selected && selected.dataset.value) return String(selected.dataset.value).trim();
    return null;
  }

  function syncMasseRadios(root, masse) {
    if (!root || !masse) return;
    const want = String(masse).toLowerCase();
    root.querySelectorAll('input.t4s-swatch-input').forEach(function (input) {
      const name = (input.name || '').toLowerCase();
      if (!MASSE_OPTION_NAMES.has(name) && !/^\d+\s*cm$/i.test(input.value || '')) return;
      input.checked = String(input.value || '').toLowerCase() === want;
    });
    root.querySelectorAll('[data-swatch-option]').forEach(function (opt) {
      const title = opt.querySelector('.t4s-swatch__title');
      if (!title) return;
      const name = title.textContent.split(':')[0].trim().toLowerCase();
      if (!MASSE_OPTION_NAMES.has(name)) return;
      opt.querySelectorAll('[data-swatch-item]').forEach(function (item) {
        const on = String(item.dataset.value || '').toLowerCase() === want;
        item.classList.toggle('is--selected', on);
      });
      const cur = opt.querySelector('[data-current-value]');
      if (cur) cur.textContent = masse;
    });
  }

  function findEckeOption(root) {
    const byClass = root.querySelector(
      '[data-swatch-option].is-t4s-name__ecke, [data-swatch-option].is-t4s-name__Ecke'
    );
    if (byClass) return byClass;
    return Array.from(root.querySelectorAll('[data-swatch-option]')).find(function (el) {
      const title = el.querySelector('.t4s-swatch__title');
      if (!title) return false;
      const name = title.textContent.split(':')[0].trim().toLowerCase();
      return ECKE_OPTION_NAMES.has(name);
    });
  }

  function rememberPreferredEcke(root, value) {
    if (!root || !value) return;
    const v = String(value).toLowerCase();
    if (v === 'links' || v === 'rechts') root._ostapPreferredEcke = v;
  }

  function capitalizeEcke(ecke) {
    const v = String(ecke || '').toLowerCase();
    if (v === 'links') return 'Links';
    if (v === 'rechts') return 'Rechts';
    return ecke;
  }

  function getVariantSelect(root) {
    return (
      (root && root.querySelector('select[name="id"]')) ||
      document.querySelector('select[name="id"]')
    );
  }

  /** Find variant id for Farbe + Ecke (+ optional Maße) from the product <select name="id"> */
  function findVariantIdForFarbeEcke(root, farbe, ecke, preferredMasse) {
    const select = getVariantSelect(root);
    if (!select || !farbe || !ecke) return null;
    const f = String(farbe).trim().toLowerCase();
    const e = String(ecke).trim().toLowerCase();
    const m = preferredMasse ? String(preferredMasse).trim().toLowerCase() : '';
    const options = select.options || [];
    let fallback = null;
    for (let i = 0; i < options.length; i++) {
      if (options[i].disabled) continue;
      const text = (options[i].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      // "Abriamo 04 / Rechts" or "Abriamo 04 / Rechts / 240cm"
      const parts = text.split(/\s*\/\s*/);
      const farbePart = (parts[0] || '').trim();
      const eckePart = (parts[1] || '').trim();
      if (farbePart !== f || eckePart !== e) continue;
      if (m) {
        const rest = parts.slice(2).map(function (p) { return p.trim(); });
        if (rest.indexOf(m) !== -1 || text.indexOf(m) !== -1) {
          return String(options[i].value);
        }
        if (!fallback) fallback = String(options[i].value);
        continue;
      }
      return String(options[i].value);
    }
    if (fallback) return fallback;
    // Fallback: loose contains (legacy titles without " / ")
    for (let i = 0; i < options.length; i++) {
      const text = (options[i].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (text.indexOf(f) === -1 || text.indexOf(e) === -1) continue;
      if (m && text.indexOf(m) === -1) continue;
      if (options[i].disabled) continue;
      return String(options[i].value);
    }
    return null;
  }

  /** Variant for a Farbe; prefers current Maße/extra when provided (Urban 240/260/280). */
  function findVariantIdForFarbe(root, farbe, preferredMasse) {
    const select = getVariantSelect(root);
    if (!select || !farbe) return null;
    const f = String(farbe).trim().toLowerCase();
    const m = preferredMasse ? String(preferredMasse).trim().toLowerCase() : '';
    const options = select.options || [];
    let fallback = null;
    for (let i = 0; i < options.length; i++) {
      if (options[i].disabled) continue;
      const text = (options[i].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const parts = text.split(/\s*\/\s*/);
      const farbePart = (parts[0] || '').trim();
      if (farbePart !== f) continue;
      if (m) {
        const rest = parts.slice(1).map(function (p) { return p.trim(); });
        if (rest.indexOf(m) !== -1) return String(options[i].value);
        if (!fallback) fallback = String(options[i].value);
        continue;
      }
      return String(options[i].value);
    }
    if (fallback) return fallback;
    for (let i = 0; i < options.length; i++) {
      const text = (options[i].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (text.indexOf(f) === -1) continue;
      if (m && text.indexOf(m) === -1) continue;
      if (options[i].disabled) continue;
      return String(options[i].value);
    }
    return null;
  }

  function getVariantPriceCentsById(root, variantId) {
    if (!variantId) return null;
    const form =
      (root && root.querySelector('form[data-type="add-to-cart-form"]')) ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    const script =
      (form && form.querySelector('script.pr_variants_json')) ||
      (root && root.querySelector('script.pr_variants_json')) ||
      document.querySelector('script.pr_variants_json');
    if (script) {
      try {
        const list = JSON.parse(script.textContent);
        if (Array.isArray(list)) {
          for (let i = 0; i < list.length; i++) {
            if (String(list[i].id) === String(variantId) && list[i].price != null) {
              return Number(list[i].price);
            }
          }
        }
      } catch (err) { /* ignore */ }
    }
    const metaVars = window.meta && window.meta.product && window.meta.product.variants;
    if (Array.isArray(metaVars)) {
      for (let i = 0; i < metaVars.length; i++) {
        if (String(metaVars[i].id) === String(variantId) && metaVars[i].price != null) {
          return Number(metaVars[i].price);
        }
      }
    }
    return null;
  }

  function syncFeaturedPriceForVariant(root, variantId) {
    const featured = root && root.getAttribute && root.hasAttribute('data-product-featured')
      ? root
      : document.querySelector('[data-product-featured]');
    if (!featured) return null;
    const price = getVariantPriceCentsById(root || featured, variantId);
    if (price == null || Number.isNaN(price)) return null;
    try {
      const data = JSON.parse(featured.getAttribute('data-product-featured') || '{}');
      data.price = price;
      featured.setAttribute('data-product-featured', JSON.stringify(data));
    } catch (err) { /* ignore */ }
    return price;
  }

  function setVariantById(root, variantId) {
    if (!variantId) return false;
    const select = getVariantSelect(root);
    const form =
      (select && select.closest('form')) ||
      (root && root.querySelector('form[data-type="add-to-cart-form"]')) ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    const id = String(variantId);

    // Kalles keeps BOTH <input name="id"> (first) and <select name="id">.
    // ATC / ostap-product-options must not keep a stale hidden input.
    if (form) {
      form.querySelectorAll('[name="id"]').forEach(function (el) {
        el.value = id;
        if (el.tagName === 'SELECT') {
          const opt = el.querySelector('option[value="' + id + '"]');
          if (opt) el.selectedIndex = opt.index;
        }
      });
    } else if (select) {
      select.value = id;
    } else {
      return false;
    }

    if (select && String(select.value) === id) {
      /* already in sync — still fire change so theme price/media update */
    }

    const priceCents = syncFeaturedPriceForVariant(root, id);

    const jq = window.jQuery_T4NT || window.jQuery;
    const target = select || (form && form.querySelector('select[name="id"]'));
    if (jq && target) {
      try {
        jq(target).val(id).trigger('change');
      } catch (err) {
        if (target) target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (target) {
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Stoff path bypasses native swatch handlers — theme may not emit variant:changed.
    const detail = { id: id, price: priceCents };
    try {
      document.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: detail }));
    } catch (err) { /* ignore */ }
    if (jq) {
      try {
        jq(document).trigger('variant:changed', [detail]);
      } catch (err) { /* ignore */ }
    }
    return true;
  }

  function syncEckeRadios(root, ecke) {
    const opt = findEckeOption(root);
    if (!opt || !ecke) return;
    const want = String(ecke).toLowerCase();
    const label = capitalizeEcke(want);
    opt.querySelectorAll('input.t4s-swatch-input').forEach(function (input) {
      const on = (input.value || '').toLowerCase() === want;
      input.checked = on;
    });
    opt.querySelectorAll('[data-swatch-item]').forEach(function (item) {
      const on = (item.dataset.value || '').toLowerCase() === want;
      item.classList.toggle('is--selected', on);
    });
    const cur = opt.querySelector('[data-current-value]');
    if (cur) cur.textContent = label;
  }

  function syncFarbeRadios(root, farbe) {
    const opt = findFarbeOption(root);
    if (!opt || !farbe) return;
    opt.querySelectorAll('input.t4s-swatch-input').forEach(function (input) {
      input.checked = input.value === farbe;
    });
    opt.querySelectorAll('[data-swatch-item]').forEach(function (el) {
      el.classList.toggle('is--selected', el.dataset.value === farbe);
    });
    const curF = opt.querySelector('[data-current-value]');
    if (curF) curF.textContent = farbe;
    root._ostapPreferredFarbe = farbe;
  }

  function rememberPreferredFarbe(root, farbe) {
    if (!root || !farbe) return;
    root._ostapPreferredFarbe = String(farbe).trim();
  }

  function getPreferredFarbe(root) {
    if (!root) return null;
    return root._ostapPreferredFarbe || getSelectedFarbe(root);
  }

  function restorePreferredEcke(root) {
    if (!root || !hasEckeOption(root) || root._ostapRestoringEcke) return;
    const preferred = root._ostapPreferredEcke;
    if (!preferred) return;

    const farbe = getSelectedFarbe(root);
    if (!farbe) return;

    const current = getSelectedEcke(root);
    if (current === preferred) {
      syncEckeRadios(root, preferred);
      return;
    }

    const variantId = findVariantIdForFarbeEcke(root, farbe, preferred);
    if (!variantId) return;

    root._ostapRestoringEcke = true;
    setVariantById(root, variantId);
    syncEckeRadios(root, preferred);
    setTimeout(function () {
      // Re-assert radios if theme rewrote DOM
      syncEckeRadios(root, preferred);
      root._ostapRestoringEcke = false;
    }, 150);
  }

  function refreshGalleryForSelection(root, farbeOverride, eckeOverride) {
    if (!root) return;
    const map = root._ostapMediaMap;
    const gallery = getGallery(root);
    if (!map || !gallery) return;
    const farbe = farbeOverride || getSelectedFarbe(root);
    const ecke = eckeOverride || root._ostapPreferredEcke || getSelectedEcke(root);
    if (!farbe) return;
    applyGalleryFilter(map, farbe, ecke, gallery);
  }

  function setupEckePreserve(root) {
    if (!root || root.dataset.ostapEckePreserve === '1') return;
    root.dataset.ostapEckePreserve = '1';

    if (hasEckeOption(root) && !root._ostapPreferredEcke) {
      rememberPreferredEcke(root, getSelectedEcke(root));
    }
    if (!root._ostapPreferredFarbe) {
      rememberPreferredFarbe(root, getSelectedFarbe(root));
    }

    const eckeOpt = hasEckeOption(root) ? findEckeOption(root) : null;
    if (eckeOpt) {
      // Intercept Ecke clicks: keep current Farbe, set Farbe×Ecke variant, sync labels.
      // Theme default often jumps to the first Farbe for the new Ecke (label desync).
      eckeOpt.addEventListener(
        'click',
        function (e) {
          if (root._ostapRestoringEcke) return;
          const item = e.target.closest('[data-swatch-item]');
          const input = e.target.closest('input.t4s-swatch-input');
          const eckeVal = (item && item.dataset.value) || (input && input.value);
          if (!eckeVal) return;

          rememberPreferredEcke(root, eckeVal);
          const farbe = getPreferredFarbe(root) || getSelectedFarbe(root);
          if (!farbe) {
            setTimeout(function () {
              refreshGalleryForSelection(root, null, eckeVal);
            }, 60);
            return;
          }

          const variantId = findVariantIdForFarbeEcke(root, farbe, eckeVal);
          if (!variantId) {
            setTimeout(function () {
              refreshGalleryForSelection(root, farbe, eckeVal);
            }, 60);
            return;
          }

          e.preventDefault();
          e.stopImmediatePropagation();

          root._ostapRestoringEcke = true;
          syncFarbeRadios(root, farbe);
          setVariantById(root, variantId);
          syncEckeRadios(root, eckeVal);
          refreshGalleryForSelection(root, farbe, eckeVal);

          setTimeout(function () {
            syncFarbeRadios(root, farbe);
            syncEckeRadios(root, eckeVal);
            refreshGalleryForSelection(root, farbe, eckeVal);
            root._ostapRestoringEcke = false;
          }, 80);
          setTimeout(function () {
            refreshGalleryForSelection(root, farbe, eckeVal);
          }, 160);
        },
        true
      );
    }

    const farbeOpt = findFarbeOption(root);
    if (farbeOpt) {
      // Intercept Farbe clicks: pick Farbe + preferred Ecke in one step
      // (theme default picks first combo = Links and resets the corner).
      farbeOpt.addEventListener(
        'click',
        function (e) {
          if (root._ostapRestoringEcke) return;
          const item = e.target.closest('[data-swatch-item]');
          if (!item || item.classList.contains('ostap-farbe--hidden')) return;

          const farbe = item.dataset.value;
          if (!farbe) return;

          rememberPreferredFarbe(root, farbe);
          rememberPreferredEcke(root, getSelectedEcke(root) || root._ostapPreferredEcke);
          const preferred = root._ostapPreferredEcke;
          const hasEcke = hasEckeOption(root);

          // Products WITHOUT Ecke: always set variant by Farbe (UI → select + hidden id)
          if (!hasEcke) {
            e.preventDefault();
            e.stopImmediatePropagation();
            syncFarbeRadios(root, farbe);
            const vid = findVariantIdForFarbe(root, farbe);
            if (vid) setVariantById(root, vid);
            refreshGalleryForSelection(root, farbe, null);
            setTimeout(function () {
              syncFarbeRadios(root, farbe);
              const vid2 = findVariantIdForFarbe(root, farbe);
              if (vid2) setVariantById(root, vid2);
              refreshGalleryForSelection(root, farbe, null);
            }, 80);
            return;
          }

          // Has Ecke but preferred corner not known yet — still set Farbe×first available,
          // then let restorePreferredEcke refine when corner is known.
          if (!preferred) {
            e.preventDefault();
            e.stopImmediatePropagation();
            syncFarbeRadios(root, farbe);
            const vid = findVariantIdForFarbe(root, farbe);
            if (vid) setVariantById(root, vid);
            refreshGalleryForSelection(root, farbe, getSelectedEcke(root));
            setTimeout(function () {
              syncFarbeRadios(root, farbe);
              restorePreferredEcke(root);
              refreshGalleryForSelection(root, farbe, root._ostapPreferredEcke || getSelectedEcke(root));
            }, 80);
            return;
          }

          const variantId = findVariantIdForFarbeEcke(root, farbe, preferred);
          if (!variantId) {
            e.preventDefault();
            e.stopImmediatePropagation();
            syncFarbeRadios(root, farbe);
            refreshGalleryForSelection(root, farbe, preferred);
            setTimeout(function () {
              syncFarbeRadios(root, farbe);
              refreshGalleryForSelection(root, farbe, preferred);
            }, 80);
            return;
          }

          e.preventDefault();
          e.stopImmediatePropagation();

          root._ostapRestoringEcke = true;

          syncFarbeRadios(root, farbe);
          setVariantById(root, variantId);
          syncEckeRadios(root, preferred);

          // Critical: our intercept blocks theme media update — refresh gallery ourselves
          refreshGalleryForSelection(root, farbe, preferred);

          setTimeout(function () {
            syncFarbeRadios(root, farbe);
            syncEckeRadios(root, preferred);
            refreshGalleryForSelection(root, farbe, preferred);
            root._ostapRestoringEcke = false;
          }, 80);
          setTimeout(function () {
            refreshGalleryForSelection(root, farbe, preferred);
          }, 160);
        },
        true
      );
    }

    root.addEventListener(
      'change',
      function (e) {
        const t = e.target;
        if (!t || !t.classList || !t.classList.contains('t4s-swatch-input')) return;
        const name = (t.name || '').toLowerCase();
        if (ECKE_OPTION_NAMES.has(name) && !root._ostapRestoringEcke) {
          rememberPreferredEcke(root, t.value);
          return;
        }
        if (COLOR_OPTION_NAMES.has(name)) {
          setTimeout(function () {
            restorePreferredEcke(root);
          }, 0);
          setTimeout(function () {
            restorePreferredEcke(root);
          }, 80);
        }
      },
      true
    );
  }

  function assignColorPair(map, key, colorPair, eckeEnabled) {
    if (!key || !colorPair.length) return;

    if (eckeEnabled && colorPair.length >= 2) {
      var rechtsSlide = null;
      var linksSlide = null;
      colorPair.forEach(function (s) {
        var alt = slideAlt(s);
        if (/\blinks\b/i.test(alt) || /(?:^|[_-\s])l(?:inks)?(?:$|[_-\s])/i.test(slideFilename(s))) {
          s.dataset.ostapEcke = 'links';
          linksSlide = s;
        } else if (/\brechts\b/i.test(alt) || /(?:^|[_-\s])p(?:$|[_-\s])|rechts/i.test(slideFilename(s))) {
          // Polish exports often use _L / _P for Links/Rechts
          s.dataset.ostapEcke = 'rechts';
          rechtsSlide = s;
        }
      });

      // Filename heuristics for Dress-Me exports: *-L / *-P
      if (!rechtsSlide || !linksSlide) {
        colorPair.forEach(function (s) {
          var fn = slideFilename(s).toLowerCase();
          if (!linksSlide && /(?:^|[_-])l(?:inks)?(?:[_-]|\.|$)/i.test(fn)) {
            s.dataset.ostapEcke = 'links';
            linksSlide = s;
          } else if (!rechtsSlide && /(?:^|[_-])p(?:[_-]|\.|$)|rechts/i.test(fn)) {
            s.dataset.ostapEcke = 'rechts';
            rechtsSlide = s;
          }
        });
      }

      if (!rechtsSlide && !linksSlide) {
        colorPair[0].dataset.ostapEcke = 'rechts';
        colorPair[1].dataset.ostapEcke = 'links';
        rechtsSlide = colorPair[0];
        linksSlide = colorPair[1];
      } else {
        if (!rechtsSlide) {
          rechtsSlide = colorPair.find(function (s) {
            return s !== linksSlide;
          }) || colorPair[0];
          rechtsSlide.dataset.ostapEcke = 'rechts';
        }
        if (!linksSlide) {
          linksSlide = colorPair.find(function (s) {
            return s !== rechtsSlide;
          }) || colorPair[1];
          linksSlide.dataset.ostapEcke = 'links';
        }
      }
      map.byFarbe[key] = rechtsSlide;
      map.byFarbeLinks[key] = linksSlide;
    } else {
      var hero = colorPair[0];
      if (hero) map.byFarbe[key] = hero;
      if (colorPair[1]) map.byFarbeLinks[key] = colorPair[1];
    }

    colorPair.forEach(function (s) {
      s.dataset.ostapFarbe = key;
      s.dataset.ostapMediaType = 'color';
    });
  }

  /** Score how clearly slides belong to a Farbe (alt / filename with fabric name). */
  function slidesMatchFarbeScore(slideList, farbe) {
    if (!farbe || !slideList || !slideList.length) return 0;
    const parsed = parseFarbeValue(farbe);
    const want = normKey(farbe);
    let score = 0;
    slideList.forEach(function (s) {
      const alt = slideAlt(s);
      const fn = slideFilename(s);
      const fromAlt = resolveFarbeFromText(alt, [farbe]);
      if (fromAlt && normKey(fromAlt) === want) {
        score += 3;
        return;
      }
      const fromFile = resolveFarbeFromFilename(fn, [farbe]);
      if (fromFile && normKey(fromFile) === want) {
        score += 3;
        return;
      }
      if (!parsed) return;
      const compact = compactKey(fn);
      const stoffCode = compactKey(parsed.stoff + parsed.code);
      const stoffCodeDash = compactKey(parsed.stoff + '-' + parsed.code);
      // Only count when fabric name + code both appear (Golden04, abriamo-01, …)
      if (stoffCode && compact.indexOf(stoffCode) !== -1) score += 2;
      else if (stoffCodeDash && compact.indexOf(stoffCodeDash) !== -1) score += 2;
    });
    return score;
  }

  /**
   * If EVERY slide clearly belongs to the same other Farbe, return it.
   * Used for Golden photos sitting after Cremona swatch (classic-before next).
   */
  function slidesDominantOtherFarbe(slideList, selfFarbe, farbeValues) {
    if (!slideList || !slideList.length || !farbeValues || !farbeValues.length) return null;
    const selfKey = normKey(selfFarbe);
    const selfScore = slidesMatchFarbeScore(slideList, selfFarbe);
    let best = null;
    let bestScore = 0;
    farbeValues.forEach(function (fv) {
      if (normKey(fv) === selfKey) return;
      const sc = slidesMatchFarbeScore(slideList, fv);
      if (sc > bestScore) {
        bestScore = sc;
        best = fv;
      }
    });
    // Need a clear win over self, and enough signal on all photos in the group
    if (!best || bestScore < 2 || bestScore <= selfScore) return null;
    if (bestScore < slideList.length * 2) return null;
    return best;
  }

  function takeColorPair(slidesList) {
    if (!slidesList || !slidesList.length) return [];
    if (slidesList.length <= 2) return slidesList.slice();
    return slidesList.slice(-2);
  }

  function markShared(map, slideList) {
    (slideList || []).forEach(function (s) {
      if (s.dataset.ostapMediaType === 'color') return;
      s.dataset.ostapMediaType = 'shared';
      map.shared.push(s);
    });
  }

  /**
   * Build Farbe → sofa photo map.
   * Supports BOTH gallery conventions:
   *  A) classic: [sofa…][swatch]  (photos before swatch)
   *  B) Cosviro-style: [swatch][sofa…] (photos after swatch)
   * Leading lifestyle shots before the first swatch stay shared when B is detected.
   */
  function orderSlidesByMedia(slides, mediaOrder) {
    if (!Array.isArray(mediaOrder) || !mediaOrder.length) return slides;
    const rank = Object.create(null);
    mediaOrder.forEach(function (id, idx) {
      rank[String(id)] = idx;
    });
    return slides.slice().sort(function (a, b) {
      const ia = rank[String(a.getAttribute('data-media-id'))];
      const ib = rank[String(b.getAttribute('data-media-id'))];
      const ra = typeof ia === 'number' ? ia : 9999;
      const rb = typeof ib === 'number' ? ib : 9999;
      return ra - rb;
    });
  }

  function buildSlideMap(gallery, eckeEnabled, farbeValues, mediaMap, fileMap, mediaOrder) {
    const slidesRaw = Array.from(gallery.querySelectorAll('[data-main-slide]'));
    // Theme/Flickity may reshuffle DOM — restore Admin media order for pairing
    const slides = orderSlidesByMedia(slidesRaw, mediaOrder);
    const map = {
      byFarbe: {},
      byFarbeLinks: {},
      hasEcke: eckeEnabled,
      shared: [],
      swatches: [],
      slides: slidesRaw,
      farbeValues: farbeValues,
    };
    fileMap = fileMap || Object.create(null);

    // 1) Alt-labeled sofa photos (explicit Farbe in alt) — highest priority
    let labeledPending = [];
    let labeledFarbe = null;

    function flushLabeled() {
      if (labeledPending.length && labeledFarbe) {
        assignColorPair(map, labeledFarbe, labeledPending, eckeEnabled);
      }
      labeledPending = [];
      labeledFarbe = null;
    }

    slides.forEach(function (slide) {
      if (isSwatchSlide(slide, mediaMap, farbeValues, fileMap)) {
        flushLabeled();
        slide.classList.add('ostap-media--swatch', 'is--media-hide');
        map.swatches.push(slide);
        return;
      }
      const altFarbe = resolveFarbeFromText(slideAlt(slide), farbeValues);
      if (altFarbe) {
        const key = normKey(altFarbe);
        if (labeledFarbe && labeledFarbe !== key) flushLabeled();
        labeledFarbe = key;
        labeledPending.push(slide);
        return;
      }
      flushLabeled();
    });
    flushLabeled();

    // 2) Segment gallery: leading | (swatch + following)* 
    const leading = [];
    const segments = [];
    let i = 0;
    while (i < slides.length) {
      const slide = slides[i];
      if (slide.classList.contains('ostap-media--swatch')) {
        const farbe = resolveSlideFarbe(slide, farbeValues, mediaMap, fileMap);
        const following = [];
        let j = i + 1;
        while (j < slides.length && !slides[j].classList.contains('ostap-media--swatch')) {
          if (slides[j].dataset.ostapMediaType !== 'color') following.push(slides[j]);
          j += 1;
        }
        segments.push({ slide: slide, farbe: farbe, following: following });
        i = j;
      } else {
        if (!segments.length && slide.dataset.ostapMediaType !== 'color') {
          leading.push(slide);
        }
        i += 1;
      }
    }

    // 3) Detect gallery convention for this product
    // post-swatch (Cosviro, no Ecke common): [swatch][sofa…][swatch][sofa…]
    // classic: [sofa…][swatch][sofa…][swatch]
    let postSwatchMode = false;
    if (segments.length) {
      const firstFarbe = segments[0].farbe;
      const after0 = segments[0].following || [];
      const leadScore = slidesMatchFarbeScore(leading, firstFarbe);
      const afterScore = slidesMatchFarbeScore(after0, firstFarbe);
      const afterOther = slidesDominantOtherFarbe(after0, firstFarbe, farbeValues);
      // Count how many swatches already have 1–2 sofa photos right after them
      var postPairs = 0;
      var preHints = 0;
      segments.forEach(function (seg, idx) {
        if (seg.following && seg.following.length && seg.following.length <= 2) postPairs += 1;
        if (idx > 0) {
          var prevFollow = segments[idx - 1].following || [];
          if (slidesMatchFarbeScore(prevFollow, seg.farbe) >= 2) preHints += 1;
        }
      });
      if (after0.length && afterScore > leadScore && !afterOther) {
        postSwatchMode = true;
      } else if (after0.length && leading.length > 2 && afterScore >= leadScore && !afterOther && postPairs >= 2 && preHints === 0) {
        postSwatchMode = true;
      } else if (after0.length && !leading.length && postPairs >= Math.max(2, Math.floor(segments.length / 2)) && preHints === 0) {
        // Theme already moved leading away — still Cosviro-style pairs after swatches
        postSwatchMode = true;
      }
    }

    let pendingBefore = [];
    if (postSwatchMode) {
      markShared(map, leading);
    } else if (leading.length) {
      pendingBefore = leading.slice();
    } else {
      markShared(map, leading);
    }

    const leadingOut = [];
    leading.forEach(function (s) {
      if (s.dataset.ostapMediaType === 'shared') {
        s.dataset.ostapMediaType = 'leading';
        leadingOut.push(s);
      }
    });

    // 4) Assign each swatch its sofa pair
    if (postSwatchMode) {
      // Strict: photos immediately AFTER a swatch belong to that Farbe.
      // Exception: whole group clearly named for another Farbe (Golden_* before Golden swatch).
      segments.forEach(function (seg) {
        const farbe = seg.farbe;
        const after = (seg.following || []).filter(function (s) {
          return s.dataset.ostapMediaType !== 'color';
        });
        if (!after.length) return;
        if (!farbe) {
          markShared(map, after);
          return;
        }

        const afterOther = slidesDominantOtherFarbe(after, farbe, farbeValues);
        const target = afterOther || farbe;
        const key = normKey(target);
        if (map.byFarbe[key]) {
          markShared(map, after);
          return;
        }
        const pair = takeColorPair(after);
        if (after.length > pair.length) markShared(map, after.slice(0, after.length - pair.length));
        assignColorPair(map, key, pair, eckeEnabled);
      });
    } else {
      segments.forEach(function (seg) {
        const farbe = seg.farbe;
        const after = seg.following || [];
        if (!farbe) {
          if (pendingBefore.length) {
            markShared(map, pendingBefore);
            pendingBefore = [];
          }
          pendingBefore = after.slice();
          return;
        }

        const key = normKey(farbe);
        if (map.byFarbe[key]) {
          if (pendingBefore.length) {
            markShared(map, pendingBefore);
            pendingBefore = [];
          }
          const afterOther = slidesDominantOtherFarbe(after, farbe, farbeValues);
          if (afterOther) pendingBefore = after.slice();
          else markShared(map, after);
          return;
        }

        const before = pendingBefore;
        const scoreBefore = slidesMatchFarbeScore(before, farbe);
        const scoreAfter = slidesMatchFarbeScore(after, farbe);
        const afterOther = slidesDominantOtherFarbe(after, farbe, farbeValues);

        let chosen = null;
        let passAfterToNext = false;

        if (afterOther) {
          if (before.length) chosen = before;
          passAfterToNext = true;
        } else if (scoreAfter > scoreBefore && after.length) {
          chosen = after;
          if (before.length) markShared(map, before);
        } else if (before.length && (scoreBefore > 0 || !after.length)) {
          chosen = before;
          passAfterToNext = after.length > 0;
        } else if (after.length) {
          chosen = after;
          if (before.length) markShared(map, before);
        } else if (before.length) {
          chosen = before;
        }

        if (chosen && chosen.length) {
          const pair = takeColorPair(chosen);
          if (chosen.length > pair.length) {
            markShared(map, chosen.slice(0, chosen.length - pair.length));
          }
          assignColorPair(map, key, pair, eckeEnabled);
        }

        pendingBefore = passAfterToNext ? after.slice() : [];
      });

      if (pendingBefore.length) markShared(map, pendingBefore);
    }

    // Trailing slides after last swatch already handled via pendingBefore;
    // any still-untyped non-swatch → shared
    slides.forEach(function (s) {
      if (s.classList.contains('ostap-media--swatch')) return;
      if (s.dataset.ostapMediaType) return;
      s.dataset.ostapMediaType = 'shared';
      map.shared.push(s);
    });

    map.leading = leadingOut.length
      ? leadingOut
      : slides.filter(function (s) {
          return s.dataset.ostapMediaType === 'leading';
        });
    map.slides = slides;
    return map;
  }

  function getSelectedFarbe(root) {
    const checked = root.querySelector(
      '.t4s-swatch-input[name="Farbe"]:checked, .t4s-swatch-input[name="farbe"]:checked, .t4s-swatch-input[name="Color"]:checked'
    );
    if (checked) return checked.value;

    for (const input of root.querySelectorAll('.t4s-swatch-input:checked')) {
      const name = (input.name || '').toLowerCase();
      if (COLOR_OPTION_NAMES.has(name)) return input.value;
    }

    const select = root.querySelector('select[name="id"]');
    if (select && select.selectedOptions[0]) {
      const title = select.selectedOptions[0].textContent || '';
      const part = title.split(' / ')[0];
      if (part) return part.trim();
    }
    return null;
  }

  function updateNavSelected(gallery) {
    var root = gallery.closest('[data-product-featured]');
    if (!root) return;
    var navContainer = root.querySelector('.t4s-carousel__nav');
    if (!navContainer) return;
    navContainer.querySelectorAll('.t4s-carousel__nav-item.is-nav-selected').forEach(function (n) {
      n.classList.remove('is-nav-selected');
    });
    var flkty = getFlickity(gallery);
    var selIdx = flkty ? flkty.selectedIndex : 0;
    var visible = navContainer.querySelectorAll('.t4s-carousel__nav-item:not(.is--media-hide)');
    if (visible[selIdx]) visible[selIdx].classList.add('is-nav-selected');
  }

  function syncNavItems(map, gallery) {
    var root = gallery.closest('[data-product-featured]');
    if (!root) return;
    var navContainer = root.querySelector('.t4s-carousel__nav');
    if (!navContainer) return;
    var navItems = navContainer.querySelectorAll('.t4s-carousel__nav-item');
    if (!navItems.length) return;

    map.slides.forEach(function (slide, i) {
      if (i >= navItems.length) return;
      var shouldHide =
        slide.classList.contains('is--media-hide') || slide.classList.contains('ostap-media--swatch');
      navItems[i].classList.toggle('is--media-hide', shouldHide);
    });

    var scroller = root.querySelector('[data-thumb__scroller]');
    if (scroller) scroller.scrollLeft = 0;

    if (!navContainer._ostapNavClick) {
      navContainer._ostapNavClick = true;
      navContainer.addEventListener('click', function (e) {
        var item = e.target.closest('.t4s-carousel__nav-item');
        if (!item || item.classList.contains('is--media-hide')) return;
        var visible = navContainer.querySelectorAll('.t4s-carousel__nav-item:not(.is--media-hide)');
        var idx = Array.prototype.indexOf.call(visible, item);
        if (idx < 0) return;
        var flkty = getFlickity(gallery);
        if (flkty && typeof flkty.select === 'function') {
          flkty.select(idx);
        }
        visible.forEach(function (nav) {
          nav.classList.remove('is-nav-selected');
        });
        item.classList.add('is-nav-selected');
      });

      var flkty = getFlickity(gallery);
      if (flkty && typeof flkty.on === 'function') {
        flkty.on('select', function () {
          var idx = flkty.selectedIndex;
          setTimeout(function () {
            navContainer.querySelectorAll('.t4s-carousel__nav-item.is-nav-selected').forEach(function (n) {
              n.classList.remove('is-nav-selected');
            });
            var visible = navContainer.querySelectorAll('.t4s-carousel__nav-item:not(.is--media-hide)');
            if (visible[idx]) visible[idx].classList.add('is-nav-selected');
          }, 0);
        });
      }
    }
  }

  function applyGalleryFilter(map, farbe, ecke, gallery) {
    if (!farbe || !map) return;
    const key = normKey(farbe);
    const eckeKey = String(ecke || '').trim().toLowerCase();

    map.slides.forEach(function (slide) {
      if (slide.classList.contains('ostap-media--swatch')) return;

      const type = slide.dataset.ostapMediaType;
      if (type === 'shared' || type === 'leading') {
        slide.classList.remove('is--media-hide');
        return;
      }

      if (type === 'color') {
        const farbeMatch = normKey(slide.dataset.ostapFarbe) === key;
        slide.classList.toggle('is--media-hide', !farbeMatch);
        return;
      }

      slide.classList.add('is--media-hide');
    });

    syncNavItems(map, gallery);
    reloadGallery(gallery);

    var hero;
    if (eckeKey === 'links') {
      hero = map.byFarbeLinks[key] || map.byFarbe[key];
    } else {
      hero = map.byFarbe[key];
    }
    if (hero) selectSlide(gallery, hero);
    else if (map.leading && map.leading[0]) selectSlide(gallery, map.leading[0]);

    updateNavSelected(gallery);
  }

  function findFarbeOption(root) {
    const byClass = root.querySelector('[data-swatch-option].is-t4s-name__farbe, [data-swatch-option].is-t4s-name__Farbe');
    if (byClass) return byClass;

    return Array.from(root.querySelectorAll('[data-swatch-option]')).find(function (el) {
      const title = el.querySelector('.t4s-swatch__title');
      if (!title) return false;
      const name = title.textContent.split(':')[0].trim().toLowerCase();
      return COLOR_OPTION_NAMES.has(name);
    });
  }

  function findSwatchInput(farbeOption, item) {
    const value = item && item.dataset ? item.dataset.value : '';
    if (!value) return null;
    const inputs = farbeOption.querySelectorAll('input.t4s-swatch-input');
    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].value === value) return inputs[i];
    }
    const forId = item.getAttribute('for');
    if (forId) return document.getElementById(forId);
    return null;
  }

  function applyStoffHighlight(root, stoff) {
    const list = root.querySelector('[data-ostap-stoff-list]');
    if (!list || !stoff) return;
    list.querySelectorAll('[data-ostap-stoff]').forEach(function (b) {
      var on = b.dataset.ostapStoff === stoff;
      b.classList.toggle('is--selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const currentLabel = root.querySelector('.ostap-stoff-farbe__stoff [data-current-value]');
    if (currentLabel) currentLabel.textContent = stoff;
  }

  function syncStoffSelection(root) {
    const farbe = getSelectedFarbe(root);
    const parsed = parseFarbeValue(farbe);
    const locked = root._ostapStoffLock;

    // While switching Stoff, keep the clicked fabric highlighted even if Farbe
    // has not updated yet (theme variant change is async).
    if (locked) {
      if (parsed && parsed.stoff === locked) {
        root._ostapStoffLock = null;
        applyStoffHighlight(root, locked);
        return;
      }
      applyStoffHighlight(root, locked);
      return;
    }

    if (!parsed) return;
    applyStoffHighlight(root, parsed.stoff);
  }

  function setupStoffFarbe(root) {
    if (!root) return false;

    const existing = root.querySelector('.ostap-stoff-farbe__stoff, [data-swatch-option].is-t4s-name__stoff');
    if (existing) {
      syncStoffSelection(root);
      return true;
    }

    const farbeOption = findFarbeOption(root);
    if (!farbeOption) return false;
    // Recreate if flag stuck after theme re-render removed our UI
    if (farbeOption.dataset.ostapStoffInit === '1' && !existing) {
      delete farbeOption.dataset.ostapStoffInit;
    }

    const values = Array.from(farbeOption.querySelectorAll('[data-swatch-item]'))
      .map(function (el) {
        return el.dataset.value;
      })
      .filter(Boolean);

    const groups = {};
    values.forEach(function (v) {
      const parsed = parseFarbeValue(v);
      if (!parsed) return;
      if (!groups[parsed.stoff]) groups[parsed.stoff] = [];
      groups[parsed.stoff].push(v);
    });

    const stoffNames = Object.keys(groups);
    if (stoffNames.length < 2) return false;

    farbeOption.dataset.ostapStoffInit = '1';
    farbeOption.classList.add('ostap-stoff-farbe__farbe');

    const stoffWrap = document.createElement('div');
    stoffWrap.className = 't4s-swatch__option ostap-stoff-farbe__stoff is-t4s-name__stoff';
    stoffWrap.setAttribute('data-swatch-option', '');
    stoffWrap.innerHTML =
      '<h4 class="t4s-swatch__title"><span>Stoff: <span data-current-value class="t4s-dib t4s-swatch__current"></span></span></h4>' +
      '<div class="t4s-swatch__list" data-ostap-stoff-list></div>';

    const list = stoffWrap.querySelector('[data-ostap-stoff-list]');
    const currentFarbe = getSelectedFarbe(root);
    let activeStoff = '';
    if (currentFarbe) {
      const p = parseFarbeValue(currentFarbe);
      if (p) activeStoff = p.stoff;
    }
    if (!activeStoff) activeStoff = stoffNames[0];

    function setActiveStoff(stoff) {
      root._ostapStoffLock = stoff;
      applyStoffHighlight(root, stoff);
      filterFarbe(stoff);
    }

    function filterFarbe(stoff) {
      farbeOption.querySelectorAll('[data-swatch-item]').forEach(function (item) {
        const parsed = parseFarbeValue(item.dataset.value);
        const show = parsed && parsed.stoff === stoff;
        item.classList.toggle('ostap-farbe--hidden', !show);
        const input = findSwatchInput(farbeOption, item);
        if (input) input.classList.toggle('ostap-farbe--hidden', !show);
      });
    }

    stoffNames.forEach(function (stoff) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        't4s-swatch__item ostap-stoff-btn' + (stoff === activeStoff ? ' is--selected' : '');
      btn.dataset.ostapStoff = stoff;
      btn.textContent = stoff;
      btn.setAttribute('aria-pressed', stoff === activeStoff ? 'true' : 'false');
      list.appendChild(btn);
    });

    if (!farbeOption.parentNode) return false;
    farbeOption.parentNode.insertBefore(stoffWrap, farbeOption);
    setActiveStoff(activeStoff);

    list.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-ostap-stoff]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const stoff = btn.dataset.ostapStoff;
      setActiveStoff(stoff);

      const current = getSelectedFarbe(root);
      const currentParsed = parseFarbeValue(current);
      if (currentParsed && currentParsed.stoff === stoff) {
        root._ostapStoffLock = null;
        applyStoffHighlight(root, stoff);
        refreshGalleryForSelection(root, current, root._ostapPreferredEcke || getSelectedEcke(root));
        return;
      }

      const firstVisible = farbeOption.querySelector('[data-swatch-item]:not(.ostap-farbe--hidden)');
      if (!firstVisible) return;

      const nextFarbe = firstVisible.dataset.value;
      const preferred = root._ostapPreferredEcke || getSelectedEcke(root);
      const preferredMasse = getSelectedMasse(root);
      const variantId =
        preferred && hasEckeOption(root)
          ? findVariantIdForFarbeEcke(root, nextFarbe, preferred, preferredMasse) ||
            findVariantIdForFarbe(root, nextFarbe, preferredMasse)
          : findVariantIdForFarbe(root, nextFarbe, preferredMasse);

      // Prefer direct variant set — programmatic .click() on swatch is flaky with theme handlers
      root._ostapRestoringEcke = true;
      farbeOption.querySelectorAll('input.t4s-swatch-input').forEach(function (input) {
        input.checked = input.value === nextFarbe;
      });
      farbeOption.querySelectorAll('[data-swatch-item]').forEach(function (el) {
        el.classList.toggle('is--selected', el.dataset.value === nextFarbe);
      });
      const curF = farbeOption.querySelector('[data-current-value]');
      if (curF) curF.textContent = nextFarbe;

      if (variantId) {
        setVariantById(root, variantId);
      } else {
        firstVisible.click();
        const input = findSwatchInput(farbeOption, firstVisible);
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (preferred && hasEckeOption(root)) syncEckeRadios(root, preferred);
      if (preferredMasse) syncMasseRadios(root, preferredMasse);
      refreshGalleryForSelection(root, nextFarbe, preferred);
      setTimeout(function () {
        if (preferred && hasEckeOption(root)) syncEckeRadios(root, preferred);
        if (preferredMasse) syncMasseRadios(root, preferredMasse);
        refreshGalleryForSelection(root, nextFarbe, preferred);
        applyStoffHighlight(root, stoff);
        syncStoffSelection(root);
        root._ostapRestoringEcke = false;
      }, 100);
      setTimeout(function () {
        refreshGalleryForSelection(root, getSelectedFarbe(root) || nextFarbe, preferred);
        syncStoffSelection(root);
      }, 180);
    });

    root.addEventListener(
      'change',
      function () {
        setTimeout(function () {
          syncStoffSelection(root);
        }, 0);
      },
      true
    );

    document.addEventListener('variant:changed', function () {
      setTimeout(function () {
        syncStoffSelection(root);
      }, 30);
    });

    return true;
  }

  function initGallery(root) {
    const gallery = getGallery(root);
    if (!gallery) return;

    const config = readConfig();
    const farbeValues = collectFarbeValues(root, config);
    const mediaMap = mediaFarbeMap(config);
    const filesMap = fileFarbeMap(config);
    const mediaOrder = Array.isArray(config.mediaOrder) ? config.mediaOrder : [];
    const eckeEnabled = hasEckeOption(root);

    try {
      const map = buildSlideMap(gallery, eckeEnabled, farbeValues, mediaMap, filesMap, mediaOrder);
      root._ostapMediaMap = map;
      const farbe = getSelectedFarbe(root);
      const ecke = eckeEnabled ? getSelectedEcke(root) : null;
      if (farbe) applyGalleryFilter(map, farbe, ecke, gallery);
    } catch (err) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[ostap-variant-media] gallery init failed', err);
      }
    }
  }

  function initProduct(root) {
    if (!root) return;

    // Stoff UI first — must not depend on gallery mapping
    try {
      setupStoffFarbe(root);
    } catch (err) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[ostap-variant-media] Stoff setup failed', err);
      }
    }

    try {
      setupEckePreserve(root);
    } catch (err2) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[ostap-variant-media] Ecke preserve failed', err2);
      }
    }

    if (root.dataset.ostapVariantMediaInit) return;
    root.dataset.ostapVariantMediaInit = '1';
    initGallery(root);
  }

  function onVariantChanged(evt) {
    const root =
      evt && evt.detail && evt.detail.sectionId
        ? document.querySelector('[data-product-featured]')
        : getProductRoot();
    if (!root) return;

    setupStoffFarbe(root);
    setupEckePreserve(root);
    if (!root.dataset.ostapVariantMediaInit) initProduct(root);

    // Theme picks first Farbe×Ecke combo (often Links) — restore shopper's Ecke
    if (!root._ostapRestoringEcke) {
      restorePreferredEcke(root);
    }

    const map = root._ostapMediaMap;
    const gallery = getGallery(root);
    if (!map || !gallery) return;

    const farbe = getSelectedFarbe(root);

    var ecke = null;
    if (map.hasEcke) {
      ecke = root._ostapPreferredEcke || getSelectedEcke(root);
      var evtEcke =
        (evt && evt.currentVariant && evt.currentVariant.option2) ||
        (evt && evt.detail && evt.detail.variant && evt.detail.variant.option2) ||
        null;
      // Prefer locked Ecke over theme's reset value
      if (!root._ostapPreferredEcke && evtEcke && /^(rechts|links)$/i.test(evtEcke)) {
        ecke = evtEcke.toLowerCase();
      }
    }

    if (farbe) {
      setTimeout(function () {
        var liveEcke = root._ostapPreferredEcke || getSelectedEcke(root) || ecke;
        applyGalleryFilter(map, getSelectedFarbe(root) || farbe, liveEcke, gallery);
      }, 50);
      setTimeout(function () {
        restorePreferredEcke(root);
        var liveEcke = root._ostapPreferredEcke || getSelectedEcke(root) || ecke;
        applyGalleryFilter(map, getSelectedFarbe(root) || farbe, liveEcke, gallery);
      }, 120);
    }
  }

  /** Keep hidden input[name=id] in sync with select — native Maße/Farbe often updates select only. */
  function syncHiddenVariantId(form) {
    if (!form) return;
    const select = form.querySelector('select[name="id"]');
    if (!select || !select.value) return;
    const id = String(select.value);
    form.querySelectorAll('input[name="id"]').forEach(function (el) {
      if (String(el.value) !== id) el.value = id;
    });
  }

  function boot() {
    const root = getProductRoot();
    if (root) initProduct(root);

    // Theme may rebuild swatches after load — retry Stoff / Ecke hooks
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      const r = getProductRoot();
      if (r) {
        setupStoffFarbe(r);
        setupEckePreserve(r);
      }
      if (tries >= 10) clearInterval(timer);
    }, 400);

    if (!document.documentElement.dataset.ostapIdSync) {
      document.documentElement.dataset.ostapIdSync = '1';
      document.addEventListener(
        'change',
        function (e) {
          const t = e.target;
          if (!t) return;
          const form =
            (t.closest && t.closest('form[data-type="add-to-cart-form"]')) ||
            document.querySelector('form[data-type="add-to-cart-form"]');
          if (!form) return;
          if (
            t.name === 'id' ||
            (t.matches && t.matches('input.t4s-swatch-input, select[name="id"]'))
          ) {
            syncHiddenVariantId(form);
          }
        },
        true
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('variant:changed', onVariantChanged);

  const jq = window.jQuery_T4NT || window.jQuery;
  if (jq) {
    jq(document).on('variant:changed', onVariantChanged);
  }
})();
